/**
 * WorkQueueContext — the "Next up" session (docs/crm-work-queue-plan.md).
 *
 * One provider above the CRM routes owns the whole guided flow: start()
 * fetches GET /api/crm/work-queue and navigates to item 1; done()/skip()
 * advance and navigate; when the list runs dry, ONE silent refill (minus the
 * leads this session already touched) — still dry means the celebration
 * state, and exit() lands back on Today. State persists in sessionStorage
 * (per-tab), so a mid-run refresh keeps the bar; detours are fine — the bar
 * floats over every CRM page and simply waits (NextUpBar renders it).
 *
 * All the math lives in workQueueLogic (pure, tested); this file is only the
 * React shell: fetch, navigate, persist.
 */
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import {
  advanceState,
  currentItem,
  orderOwnWorkFirst,
  doneCount,
  rowStatus,
  ownersParam,
  parseStoredState,
  progress,
  serializeState,
  sessionExcludes,
  skipState,
  withRefill,
  type OwnerSelection,
  type QueueRowStatus,
  type WorkQueueItem,
  type WorkQueueState,
} from './workQueueLogic';

const STORE_KEY = 'crm.workQueue.v1';

interface WorkQueueCtx {
  /** A session is running (the bar is up — including the finished state). */
  active: boolean;
  /** The list ran dry after a refill — celebration state until exit(). */
  finished: boolean;
  starting: boolean;
  item: WorkQueueItem | null;
  position: number;
  total: number;
  /** The whole queue, each row tagged done / skipped / current / upcoming —
   *  what the bar's expandable list renders. */
  rows: { item: WorkQueueItem; status: QueueRowStatus }[];
  /** How many rows have been worked (the list's progress line). */
  done_count: number;
  /** Jump straight to a row without marking anything. */
  goTo: (index: number) => void;
  /**
   * Start a session; resolves to the queue size (0 = nothing to work).
   * `owners` is the picker's selection — omitted means the server's default
   * (the caller's leads + unassigned); [] means network-wide.
   */
  start: (source?: 'safety_net', owners?: OwnerSelection) => Promise<number>;
  done: () => void;
  skip: () => void;
  /**
   * Defer the lead on deck until `untilIso`, then advance as if Done. Unlike
   * done(), this one PERSISTS: it writes crm.contacts.snoozed_until, so the
   * lead stays off the queue across sessions until the snooze passes (or the
   * patient replies, which clears it server-side).
   */
  snoozeCurrent: (untilIso: string) => void;
  snoozing: boolean;
  /** Set when the snooze PATCH failed — the bar stays on the lead. */
  snoozeError: string | null;
  /** End the session. goHome lands on Today (the celebration's button). */
  exit: (goHome?: boolean) => void;
}

const WorkQueueContext = createContext<WorkQueueCtx>({
  active: false, finished: false, starting: false, item: null, position: 0, total: 0,
  rows: [], done_count: 0, goTo: () => {},
  start: async () => 0, done: () => {}, skip: () => {},
  snoozeCurrent: () => {}, snoozing: false, snoozeError: null, exit: () => {},
});

export function WorkQueueProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  // The viewer's id, only so an explicit owners list can still opt into
  // out-of-office coverage (workQueueLogic.ownersParam explains why).
  const { user } = useAuth();
  const meId = user?.id ?? null;
  const [state, setState] = useState<WorkQueueState | null>(
    () => parseStoredState(window.sessionStorage.getItem(STORE_KEY))
  );
  const [finished, setFinished] = useState(false);
  const [starting, setStarting] = useState(false);
  const [snoozing, setSnoozing] = useState(false);
  const [snoozeError, setSnoozeError] = useState<string | null>(null);

  useEffect(() => {
    if (state) window.sessionStorage.setItem(STORE_KEY, serializeState(state));
    else window.sessionStorage.removeItem(STORE_KEY);
  }, [state]);

  const fetchQueue = useCallback(async (
    source: 'all' | 'safety_net',
    owners: OwnerSelection | undefined,
  ): Promise<WorkQueueItem[]> => {
    const res = await api.get('/api/crm/work-queue', {
      params: {
        ...(source === 'safety_net' ? { source: 'safety_net' } : {}),
        // meId is what lets ownersParam re-assert the coverage default on an
        // EXPLICIT list (see workQueueLogic.ownersParam) — without it the
        // guided flow would silently skip every lead the viewer is covering.
        ...(owners ? { owners: ownersParam(owners, meId) } : {}),
      },
    });
    return (res.data?.items || []) as WorkQueueItem[];
  }, [meId]);

  const start = useCallback(async (source?: 'safety_net', owners?: OwnerSelection): Promise<number> => {
    setStarting(true);
    try {
      const fetched = await fetchQueue(source || 'all', owners);
      if (!fetched.length) return 0;
      // Own work before covered work. withRefill already does this for the
      // silent refill; without it here, the FIRST pass through a widened queue
      // keeps raw server order and a teammate's covered lead can sit ahead of
      // the viewer's own — the rule would appear to switch on halfway through
      // a session, which reads as a bug rather than a policy.
      const items = orderOwnWorkFirst(fetched);
      setState({
        source: source || 'all',
        owners: owners ?? null,
        items,
        index: 0,
        handled: [],
        skipped: [],
        startedAt: new Date().toISOString(),
      });
      setFinished(false);
      setSnoozeError(null);
      navigate(items[0].href);
      return items.length;
    } catch (e: any) {
      console.error('[workQueue] start failed:', e?.message);
      return 0;
    } finally {
      setStarting(false);
    }
  }, [fetchQueue, navigate]);

  const step = useCallback(async (skip: boolean) => {
    if (!state) return;
    setSnoozeError(null);
    const next = skip ? skipState(state) : advanceState(state);
    const nextItem = currentItem(next);
    if (nextItem) {
      setState(next);
      navigate(nextItem.href);
      return;
    }
    // Exhausted — one silent refill minus everything this session touched.
    // Acted-on leads drop out server-side anyway (their coverage is restored);
    // the exclude set covers the sweep/trigger lag.
    try {
      const fetched = await fetchQueue(next.source, next.owners ?? undefined);
      const exclude = sessionExcludes(next);
      const fresh = fetched.filter((i) => !exclude.has(i.contact_id));
      if (fresh.length) {
        const refilled = withRefill(next, fresh);
        setState(refilled);
        navigate(fresh[0].href);
        return;
      }
    } catch (e: any) {
      console.error('[workQueue] refill failed:', e?.message);
    }
    setState(next);
    setFinished(true);
  }, [state, fetchQueue, navigate]);

  /**
   * Snooze the lead on deck, then advance. The advance runs ONLY after the
   * PATCH lands: moving on from a lead we failed to defer would hand them
   * straight back on the next refill, which is the whole thing this fixes.
   * Counted as `handled` (not skipped) — deciding a lead needs nothing IS
   * working it.
   */
  const snoozeCurrent = useCallback(async (untilIso: string) => {
    const cur = currentItem(state);
    if (!cur || snoozing) return;
    setSnoozing(true);
    setSnoozeError(null);
    try {
      await api.patch(`/api/crm/contacts/${cur.contact_id}`, { snoozed_until: untilIso });
    } catch (e: any) {
      console.error('[workQueue] snooze failed:', e?.message);
      setSnoozeError('Could not snooze this lead — try again.');
      return;
    } finally {
      setSnoozing(false);
    }
    await step(false);
  }, [state, snoozing, step]);

  const exit = useCallback((goHome = false) => {
    setState(null);
    setFinished(false);
    setSnoozeError(null);
    window.sessionStorage.removeItem(STORE_KEY);
    if (goHome) navigate('/crm');
  }, [navigate]);

  /** Jump to a row the recruiter picked out of the list. Marks nothing —
   *  browsing the queue is not the same as working it. */
  const goTo = useCallback((index: number) => {
    if (!state) return;
    const target = state.items[index];
    if (!target) return;
    setState({ ...state, index });
    setFinished(false);
    navigate(target.href);
  }, [state, navigate]);

  const { position, total } = progress(state);
  const rows = state
    ? state.items.map((item, i) => ({ item, status: rowStatus(state, i) }))
    : [];
  return (
    <WorkQueueContext.Provider
      value={{
        active: !!state,
        finished,
        starting,
        item: currentItem(state),
        position,
        total,
        rows,
        done_count: doneCount(state),
        goTo,
        start,
        done: () => { void step(false); },
        skip: () => { void step(true); },
        snoozeCurrent: (untilIso: string) => { void snoozeCurrent(untilIso); },
        snoozing,
        snoozeError,
        exit,
      }}
    >
      {children}
    </WorkQueueContext.Provider>
  );
}

export function useWorkQueue(): WorkQueueCtx {
  return useContext(WorkQueueContext);
}
